import {
  EMPTY_RETAIL_KPI_METRICS,
  resolveRetailKpiMetrics,
} from '@/utils/workspaceAdProductMetrics';

export type ProductReportMetric = 'total' | 'ad' | 'organic';

export type ProductReportFilter = 'all' | 'winners' | 'losers';

export type ProductReportAdRow = {
  asin: string;
  productName?: string | null;
  imageUrl?: string | null;
  sales: number;
};

export type ProductReportVendorSale = {
  asin: string;
  orderedRevenue: number;
};

export type ProductReportCatalogItem = {
  asin: string;
  title?: string | null;
  imageUrl?: string | null;
};

export type ProductReportSources = {
  vendorMapped: boolean;
  currentAds: ProductReportAdRow[];
  previousAds: ProductReportAdRow[];
  currentVendorSales: ProductReportVendorSale[];
  previousVendorSales: ProductReportVendorSale[];
  catalog: ProductReportCatalogItem[];
};

export type ProductReportPeriodValues = {
  current: number | null;
  previous: number | null;
  /** Null when a side is missing or the previous value is 0. */
  pctChange: number | null;
};

export type ProductReportProduct = {
  asin: string;
  productName: string;
  imageUrl: string | null;
  total: ProductReportPeriodValues;
  ad: ProductReportPeriodValues;
  organic: ProductReportPeriodValues;
};

export type RankedProductReportRow = ProductReportProduct & {
  rank: number;
};

type Bucket = {
  asin: string;
  productName: string;
  imageUrl: string | null;
  currentAd: number;
  previousAd: number;
  currentVendor: number;
  previousVendor: number;
};

function asinKey(asin: string): string {
  return asin.trim().toUpperCase();
}

function num(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function usefulName(name: string | null | undefined, asin: string): string | null {
  const trimmed = (name ?? '').trim();
  if (!trimmed || trimmed.toUpperCase() === asinKey(asin)) return null;
  return trimmed;
}

function ensureBucket(map: Map<string, Bucket>, asin: string): Bucket | null {
  const key = asinKey(asin);
  if (!key) return null;
  const existing = map.get(key);
  if (existing) return existing;
  const bucket: Bucket = {
    asin: asin.trim(),
    productName: '',
    imageUrl: null,
    currentAd: 0,
    previousAd: 0,
    currentVendor: 0,
    previousVendor: 0,
  };
  map.set(key, bucket);
  return bucket;
}

function applyIdentity(
  bucket: Bucket,
  asin: string,
  name: string | null | undefined,
  imageUrl: string | null | undefined,
  preferAsin: boolean
) {
  if (preferAsin) bucket.asin = asin.trim();
  const nextName = usefulName(name, bucket.asin);
  if (nextName) bucket.productName = nextName;
  const nextImage = (imageUrl ?? '').trim();
  if (nextImage) bucket.imageUrl = nextImage;
}

/** Percent change versus the previous period. Previous 0 stays blank. */
export function productReportPctChange(
  current: number | null,
  previous: number | null
): number | null {
  if (current == null || previous == null || previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function periodValues(current: number | null, previous: number | null): ProductReportPeriodValues {
  return {
    current,
    previous,
    pctChange: productReportPctChange(current, previous),
  };
}

function splitPeriod(adSales: number, vendorSales: number, vendorMapped: boolean) {
  if (!vendorMapped) {
    return { total: null as number | null, ad: adSales, organic: null as number | null };
  }
  const resolved = resolveRetailKpiMetrics(
    { cost: 0, sales30d: adSales, purchases30d: 0 },
    { ...EMPTY_RETAIL_KPI_METRICS, totalSales: vendorSales }
  );
  return {
    total: resolved.totalSales,
    ad: resolved.paidSales,
    organic: resolved.organicSales,
  };
}

function hasActivity(bucket: Bucket, vendorMapped: boolean): boolean {
  if (bucket.currentAd > 0 || bucket.previousAd > 0) return true;
  if (!vendorMapped) return false;
  return bucket.currentVendor > 0 || bucket.previousVendor > 0;
}

/** Join advertised-product sales with vendor retail sales into one child-ASIN row. */
export function buildProductReport(sources: ProductReportSources): ProductReportProduct[] {
  const map = new Map<string, Bucket>();

  for (const item of sources.catalog) {
    const bucket = ensureBucket(map, item.asin);
    if (!bucket) continue;
    applyIdentity(bucket, item.asin, item.title, item.imageUrl, false);
  }
  for (const row of sources.previousAds) {
    const bucket = ensureBucket(map, row.asin);
    if (!bucket) continue;
    bucket.previousAd += num(row.sales);
    applyIdentity(bucket, row.asin, row.productName, row.imageUrl, true);
  }
  for (const row of sources.currentAds) {
    const bucket = ensureBucket(map, row.asin);
    if (!bucket) continue;
    bucket.currentAd += num(row.sales);
    applyIdentity(bucket, row.asin, row.productName, row.imageUrl, true);
  }
  if (sources.vendorMapped) {
    for (const row of sources.previousVendorSales) {
      const bucket = ensureBucket(map, row.asin);
      if (!bucket) continue;
      bucket.previousVendor += num(row.orderedRevenue);
    }
    for (const row of sources.currentVendorSales) {
      const bucket = ensureBucket(map, row.asin);
      if (!bucket) continue;
      bucket.currentVendor += num(row.orderedRevenue);
    }
  }

  const products: ProductReportProduct[] = [];
  for (const bucket of map.values()) {
    if (!hasActivity(bucket, sources.vendorMapped)) continue;
    const current = splitPeriod(bucket.currentAd, bucket.currentVendor, sources.vendorMapped);
    const previous = splitPeriod(bucket.previousAd, bucket.previousVendor, sources.vendorMapped);
    products.push({
      asin: bucket.asin,
      productName: bucket.productName || bucket.asin,
      imageUrl: bucket.imageUrl,
      total: periodValues(current.total, previous.total),
      ad: periodValues(current.ad, previous.ad),
      organic: periodValues(current.organic, previous.organic),
    });
  }
  products.sort((a, b) => a.asin.localeCompare(b.asin));
  return products;
}

export function matchesProductReportSearch(product: ProductReportProduct, search: string): boolean {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  return (
    product.asin.toLowerCase().includes(query) || product.productName.toLowerCase().includes(query)
  );
}

/** Winners are strictly up. Losers are strictly down. Flat and incomparable rows stay in All. */
export function matchesProductReportFilter(
  pctChange: number | null,
  filter: ProductReportFilter
): boolean {
  if (filter === 'all') return true;
  if (pctChange == null || pctChange === 0) return false;
  return filter === 'winners' ? pctChange > 0 : pctChange < 0;
}

/** Rank one metric after search and the winner/loser filter. Highest current amount is rank 1. */
export function rankProductReport(
  products: ProductReportProduct[],
  metric: ProductReportMetric,
  filter: ProductReportFilter,
  search: string
): RankedProductReportRow[] {
  const rows = products.filter(
    (product) =>
      matchesProductReportSearch(product, search) &&
      matchesProductReportFilter(product[metric].pctChange, filter)
  );
  rows.sort((a, b) => {
    const av = a[metric].current ?? Number.NEGATIVE_INFINITY;
    const bv = b[metric].current ?? Number.NEGATIVE_INFINITY;
    if (bv !== av) return bv - av;
    return a.asin.localeCompare(b.asin);
  });
  return rows.map((product, index) => ({ ...product, rank: index + 1 }));
}
